import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  CreditCard,
  Banknote,
  Smartphone,
  ArrowRightLeft,
  Wallet,
} from 'lucide-react';
import { toast } from 'sonner';

const METHODS = [
  {
    id: 'qb_link',
    icon: Wallet,
    color: 'text-green-600',
    label: { ru: 'QB Payment Link', en: 'QB Payment Link' },
    desc: { ru: 'Ссылка на оплату через QuickBooks', en: 'Payment link via QuickBooks' },
  },
  {
    id: 'cash',
    icon: Banknote,
    color: 'text-emerald-600',
    label: { ru: 'Наличные', en: 'Cash' },
    desc: { ru: 'Клиент платит наличными', en: 'Client pays cash' },
  },
  {
    id: 'e_transfer',
    icon: ArrowRightLeft,
    color: 'text-blue-600',
    label: { ru: 'e-Transfer / Interac', en: 'e-Transfer / Interac' },
    desc: { ru: 'Банковский перевод', en: 'Bank transfer' },
  },
  {
    id: 'tap_to_pay',
    icon: Smartphone,
    color: 'text-purple-600',
    label: { ru: 'Tap to Pay (iPhone)', en: 'Tap to Pay (iPhone)' },
    desc: { ru: 'Через GoPayment на iPhone', en: 'Via GoPayment on iPhone' },
  },
  {
    id: 'stripe',
    icon: CreditCard,
    color: 'text-indigo-600',
    label: { ru: 'Stripe (резерв)', en: 'Stripe (backup)' },
    desc: { ru: 'Ссылка на оплату через Stripe', en: 'Payment link via Stripe' },
  },
];

export function PaymentMethodSelector({
  order,
  remainingAmount,
  api,
  onPaymentLink,
  onCashPayment,
  onStripeCheckout,
  onDone,
}) {
  const { lang } = useLanguage();
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [customAmount, setCustomAmount] = useState('');

  const amount = customAmount ? parseFloat(customAmount) : remainingAmount;

  const handleConfirm = async () => {
    if (!selected) return;
    if (amount <= 0) {
      toast.error(lang === 'ru' ? 'Укажите сумму' : 'Enter amount');
      return;
    }

    setLoading(true);
    try {
      switch (selected) {
        case 'qb_link': {
          const res = await api.post(`/qb/payment/${order.id}`);
          onPaymentLink?.(res.data.payment_link, 'quickbooks');
          break;
        }
        case 'cash':
        case 'e_transfer':
        case 'tap_to_pay': {
          const res = await api.post(`/qb/record-cash/${order.id}`, {
            amount: amount,
            method: selected,
            note: selected === 'tap_to_pay'
              ? 'Tap to Pay (GoPayment)'
              : selected === 'e_transfer'
                ? 'e-Transfer / Interac'
                : 'Cash',
          });
          toast.success(
            lang === 'ru'
              ? `Оплата $${res.data.amount.toFixed(2)} записана`
              : `Payment $${res.data.amount.toFixed(2)} recorded`
          );
          if (!res.data.qb_synced) {
            toast.info(
              lang === 'ru'
                ? 'QB не подключён — записано только в CRM'
                : 'QB not connected — recorded in CRM only'
            );
          }
          onCashPayment?.(res.data);
          break;
        }
        case 'stripe': {
          onStripeCheckout?.();
          break;
        }
        default:
          break;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Method Selection */}
      <div className="space-y-2">
        {METHODS.map((method) => {
          const Icon = method.icon;
          const isSelected = selected === method.id;
          return (
            <button
              key={method.id}
              type="button"
              className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                isSelected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border hover:bg-muted/50'
              }`}
              onClick={() => setSelected(method.id)}
            >
              <div className={`h-9 w-9 rounded-lg bg-muted flex items-center justify-center shrink-0`}>
                <Icon className={`h-5 w-5 ${method.color}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm">{method.label[lang]}</div>
                <div className="text-xs text-muted-foreground">{method.desc[lang]}</div>
              </div>
              {isSelected && (
                <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center shrink-0">
                  <div className="h-2 w-2 rounded-full bg-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Custom Amount (for cash/e-transfer/tap) */}
      {selected && selected !== 'qb_link' && selected !== 'stripe' && (
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">
            {lang === 'ru' ? 'Сумма (оставьте пустым для полной оплаты)' : 'Amount (leave empty for full payment)'}
          </label>
          <Input
            type="number"
            step="0.01"
            min="0.01"
            placeholder={`$${remainingAmount.toFixed(2)}`}
            value={customAmount}
            onChange={(e) => setCustomAmount(e.target.value)}
          />
        </div>
      )}

      {/* Confirm Button */}
      <Button
        className="w-full"
        onClick={handleConfirm}
        disabled={!selected || loading}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
        {selected === 'qb_link'
          ? (lang === 'ru' ? 'Создать ссылку на оплату' : 'Create payment link')
          : selected === 'stripe'
            ? (lang === 'ru' ? 'Создать Stripe ссылку' : 'Create Stripe link')
            : (lang === 'ru' ? `Подтвердить оплату $${amount.toFixed(2)}` : `Confirm payment $${amount.toFixed(2)}`)
        }
      </Button>
    </div>
  );
}
